import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, from, of, forkJoin } from 'rxjs';
import { map, switchMap, catchError, take } from 'rxjs/operators';
import firebase from 'firebase';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ZonesSlotService } from './zones-slot.service';
import { SessionStatusType, TransactionType } from '../enum/enum';
import { EventEmitter } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  // Single data stream for historical analytics
  private parkingLogsData = new BehaviorSubject<any[]>([]);
  public parkingLogs$ = this.parkingLogsData.asObservable();
  
  // Separate data stream for active sessions (real-time only)
  private activeSessionsData = new BehaviorSubject<any[]>([]);
  public activeSessions$ = this.activeSessionsData.asObservable();
  
  // Loading state management
  private loadingState = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingState.asObservable();
  
  private account: any;
  private zone_id: string;
  private startDate: firebase.firestore.Timestamp;
  private endDate: firebase.firestore.Timestamp;
  private isLoading: boolean = false;
  private isLoadingHistorical: boolean = false;
  private firestoreUnsubscribe: () => void = null;
  private historicalUnsubscribe: () => void = null;
  private setDateRangeTimeout: any = null;
  
  // Store last loaded date range to avoid duplicate loads
  private _lastLoadedStartDate: firebase.firestore.Timestamp;
  private _lastLoadedEndDate: firebase.firestore.Timestamp;
  private _lastLoadedZoneId: string;
  
  // Store vehicle types from account settings
  private vehicleTypes: any[] = [];
  private vehicleTypeMap = new Map<string, string>();
  
  // Transaction type mapping for vehicle categorization
  private transactionTypeMap = new Map<number, string>();
  
  // Event emitter for date range changes
  public dateRangeChanged = new EventEmitter<void>();
  
  constructor(
    private router: Router,
    private zonesSlotService: ZonesSlotService
  ) {
    this.account = JSON.parse(localStorage.getItem('account'));
    
    // Get zone ID from URL if possible
    const urlParts = this.router.routerState.snapshot.url.split('/');
    this.zone_id = urlParts[urlParts.length - 1];
    
    if (this.zone_id === 'dashboard' && this.account?.settings?.zones?.length > 0) {
      this.zone_id = this.account.settings.zones[0].zone_id;
    }
    
    // Initialize with today's date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.startDate = firebase.firestore.Timestamp.fromDate(today);
    
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);
    this.endDate = firebase.firestore.Timestamp.fromDate(endOfToday);
    
    // Initialize with empty arrays
    this.parkingLogsData.next([]);
    this.activeSessionsData.next([]);
    
    // Subscribe to route changes to update zone_id and reload logs
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd)
    ).subscribe(async (res) => {
      const urlParts = res.url.split('/');
      let newZoneId = urlParts[urlParts.length - 1];
      
      // Only update zone_id if it's different from 'dashboard' (indicates an actual zone ID)
      if (newZoneId !== 'dashboard') {
        if (this.zone_id !== newZoneId) {
          this.zone_id = newZoneId;
          if(this.zone_id) await this.loadParkbotLogs(this.account.account_key);
        }
      }
    });
    
    // Load vehicle types from account settings and initialize transaction type mapping
    if (this.account) {
      this.loadVehicleTypes();
      this.initializeTransactionTypeMapping();
    }
    
    // Load logs after a short delay to ensure everything is initialized
    setTimeout(() => {
      this.loadParkbotLogs(this.account.account_key);
    }, 100);
  }

  // Set loading state
  private setLoading(loading: boolean): void {
    this.loadingState.next(loading);
  }

  // Load vehicle types from account settings
  private async loadVehicleTypes(): Promise<void> {
    try {
      // Get account from localStorage
      const accountData = localStorage.getItem('account');
      if (!accountData) {
      return;
    }
    
      const account = JSON.parse(accountData);
      this.account = account;
    
      // Get vehicle types from account settings
      if (account && account.settings && account.settings.zones) {
        // Extract vehicle types from all zones
        const uniqueVehicleTypes = new Map();
        
        // Filter zones by current zone_id if set
        const zones = this.zone_id && this.zone_id !== 'dashboard'
          ? account.settings.zones.filter(zone => zone.zone_id === this.zone_id)
          : account.settings.zones;
        
        // Loop through each zone and collect vehicle types
        zones.forEach(zone => {
          if (zone.customizeRates && Array.isArray(zone.customizeRates)) {
            zone.customizeRates.forEach(rate => {
              if (!uniqueVehicleTypes.has(rate.vehicle_type)) {
                uniqueVehicleTypes.set(rate.vehicle_type, {
                  type: rate.vehicle_type,
                  name: rate.vehicle_type_custom_name || rate.vehicle_type_name || rate.vehicle_type,
                  image: rate.vehicle_image || `assets/img/${rate.vehicle_type}.png`
                });
              }
            });
          }
        });
        
        // Convert to array
        this.vehicleTypes = Array.from(uniqueVehicleTypes.values());
        
        // Create mapping for vehicle type categorization
        this.vehicleTypeMap.clear();
        this.vehicleTypes.forEach(vt => {
          this.vehicleTypeMap.set(vt.type, vt.type);
          
          // Add common variations/aliases
          if (vt.type === 'light_vehicle') {
            this.vehicleTypeMap.set('car', vt.type);
            this.vehicleTypeMap.set('private car', vt.type);
            this.vehicleTypeMap.set('private_car', vt.type);
            this.vehicleTypeMap.set('sedan', vt.type);
          } else if (vt.type === 'medium_vehicle') {
            this.vehicleTypeMap.set('suv', vt.type);
            this.vehicleTypeMap.set('van', vt.type);
            this.vehicleTypeMap.set('utility', vt.type);
            this.vehicleTypeMap.set('light utility', vt.type);
            this.vehicleTypeMap.set('light_utility', vt.type);
          } else if (vt.type === 'heavy_vehicle') {
            this.vehicleTypeMap.set('truck', vt.type);
            this.vehicleTypeMap.set('bus', vt.type);
            this.vehicleTypeMap.set('light truck', vt.type);
            this.vehicleTypeMap.set('light_truck', vt.type);
          }
        });
      } else {
      }
    } catch (error) {
    }
  }
  
  // Initialize transaction type mapping
  private initializeTransactionTypeMapping(): void {
    this.transactionTypeMap.set(TransactionType.SelfDrive, 'Self Drive');
    this.transactionTypeMap.set(TransactionType.ValetDrive, 'Valet');
    this.transactionTypeMap.set(TransactionType.VipPaid, 'VIP Paid');
    this.transactionTypeMap.set(TransactionType.VipFree, 'VIP Free');
    this.transactionTypeMap.set(TransactionType.VipValet, 'VIP Valet');
  }
  
  // Helper method to get transaction type name
  private getTransactionTypeName(transactionType: number): string {
    return this.transactionTypeMap.get(transactionType) || 'Unknown';
  }
  
  // Helper method to get transaction type category name
  private getTransactionTypeCategory(transactionType: number, vehicleType?: string): string {
    // Prioritize transaction type over vehicle type for categorization
    switch (transactionType) {
      case TransactionType.ValetDrive:
        return 'valet_light_vehicle'; // Maps to "Valet (Guest)"
      case TransactionType.VipPaid:
        return 'vip_light_vehicle'; // Maps to "Doctor (Paid)"
      case TransactionType.VipFree:
        return 'vip_free_light_vehicle'; // Maps to "Doctor (Free)"
      case TransactionType.VipValet:
        return 'vip_valet_light_vehicle'; // Maps to "VIP Valet" (if needed)
      case TransactionType.SelfDrive:
      default:
        // For self-drive, use the base vehicle type
        return this.categorizeVehicleType(vehicleType || '');
    }
  }

  // Helper method to get display name for categories
  private getCategoryDisplayName(category: string): string {
    const categoryNames = {
      'valet_light_vehicle': 'Valet (Guest)',
      'vip_light_vehicle': 'Doctor (Paid)', 
      'vip_free_light_vehicle': 'Doctor (Free)',
      'vip_valet_light_vehicle': 'VIP Valet',
      'light_vehicle': 'Car',
      'medium_vehicle': 'Medium Vehicle',
      'heavy_vehicle': 'Heavy Vehicle',
      'motorcycle': 'Motorcycle',
      'tricycle': 'Tricycle',
      'bicycle': 'Bicycle',
      'unknown': 'Unknown'
    };
    
    return categoryNames[category] || category.toUpperCase();
  }
  
  // Helper method to categorize vehicle types consistently
  private categorizeVehicleType(vehicleType: string): string {
    if (!vehicleType) return 'unknown';
    
    // Clean up the input
    const cleanType = vehicleType.trim().toLowerCase();
    
    // First check if we have an exact match in our map
    if (this.vehicleTypeMap.has(cleanType)) {
      return this.vehicleTypeMap.get(cleanType);
    }
    
    // If no exact match, try to find a partial match
    for (const [key, value] of this.vehicleTypeMap.entries()) {
      if (cleanType.includes(key) || key.includes(cleanType)) {
        return value;
      }
    }
    
    // If we still don't have a match, use some common fallbacks
    if (cleanType.includes('car') || cleanType.includes('sedan') || cleanType.includes('private')) {
      return 'light_vehicle';
    } else if (cleanType.includes('suv') || cleanType.includes('van') || cleanType.includes('utility')) {
      return 'medium_vehicle';
    } else if (cleanType.includes('truck') || cleanType.includes('bus') || cleanType.includes('heavy')) {
      return 'heavy_vehicle';
    } else if (cleanType.includes('motor') || cleanType.includes('bike')) {
      return 'motorcycle';
    } else if (cleanType.includes('tri') || cleanType.includes('three')) {
      return 'tricycle';
    } else if (cleanType.includes('vip')) {
      return 'vip_vehicle';
    } else if (cleanType.includes('valet')) {
      return 'valet_vehicle';
    }
    
    // If all else fails
    return 'unknown';
  }

  // Update date range and trigger refresh (debounced)
  setDateRange(startDate: firebase.firestore.Timestamp, endDate: firebase.firestore.Timestamp, account_key: string): void {
    // Clear any pending date range updates
    if (this.setDateRangeTimeout) {
      clearTimeout(this.setDateRangeTimeout);
    }
    
    // Check if this is the exact same date range we already have loaded
    const isDateRangeEqual = (date1: firebase.firestore.Timestamp, date2: firebase.firestore.Timestamp) => {
      return date1 && date2 && date1.seconds === date2.seconds && date1.nanoseconds === date2.nanoseconds;
    };
    
    // Skip if exact same date range and zone - no need to reload
    if (this.historicalUnsubscribe && 
        isDateRangeEqual(this.startDate, startDate) &&
        isDateRangeEqual(this.endDate, endDate) &&
        this._lastLoadedZoneId === this.zone_id) {
      return;
    }
    
    // Update the date range immediately
    this.startDate = startDate;
    this.endDate = endDate;
    
    // Debounce the heavy operations to prevent rapid successive calls
    this.setDateRangeTimeout = setTimeout(() => {
      this.setLoading(true);
      this.loadHistoricalData(account_key);
      this.dateRangeChanged.emit();
      this.setDateRangeTimeout = null;
    }, 300); // 300ms debounce
  }
  
  // Method to set zone ID filter
  setZoneId(zoneId: string): void {
    if (this.zone_id !== zoneId) {
      this.setLoading(true);
      this.zone_id = zoneId;
      
      // Restart real-time updates and refresh historical data if account is available
      if (this.account) {
        this.stopRealtimeUpdates();
        // Reload vehicle types and transaction mapping for the new zone
        this.loadVehicleTypes();
        this.initializeTransactionTypeMapping();
        this.startRealtimeUpdates(this.account.account_key);
        this.loadHistoricalData(this.account.account_key);
      }
    }
  }

  // Legacy method for backward compatibility
  async loadParkbotLogs(account_key: string): Promise<void> {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.setLoading(true);
    this.account = { account_key };

    try {
      // Load vehicle types for categorization and initialize transaction type mapping
      await this.loadVehicleTypes();
      this.initializeTransactionTypeMapping();
      
      // Start real-time updates for active sessions
      this.startRealtimeUpdates(account_key);
      
      // Load historical data for analytics
      this.loadHistoricalData(account_key);
    } catch (error) {
      this.setLoading(false);
    } finally {
      this.isLoading = false;
    }
  }

  getStartOfToday() {
    const now = new Date();
    now.setHours(0, 0, 0, 0); 
    return firebase.firestore.Timestamp.fromDate(now);
  }

  // 1. Peak Hour Analysis - Uses historical data
  getPeakHourAnalysis(): Observable<any> {
    return this.parkingLogs$.pipe(
      map(logs => {
        const hourCounts = Array(24).fill(0).map(() => ({
          entries: 0,
          exits: 0,
          occupancy: 0
        }));
        
        // Process entries by hour
        logs.forEach(log => {
          if (log.time_in && log.time_in.seconds) {
            const entryHour = new Date(log.time_in.seconds * 1000).getHours();
            hourCounts[entryHour].entries++;
          }
          
          if (log.time_out && log.time_out.seconds) {
            const exitHour = new Date(log.time_out.seconds * 1000).getHours();
            hourCounts[exitHour].exits++;
          }
        });
        
        // Calculate occupancy (entries - exits) for each hour
        let runningOccupancy = 0;
        hourCounts.forEach((hour, index) => {
          runningOccupancy += hour.entries - hour.exits;
          hourCounts[index].occupancy = runningOccupancy > 0 ? runningOccupancy : 0;
        });
        
        // Find peak hours
        const peakEntryHour = hourCounts.reduce((max, hour, index) => 
          hour.entries > hourCounts[max].entries ? index : max, 0);
          
        const peakExitHour = hourCounts.reduce((max, hour, index) => 
          hour.exits > hourCounts[max].exits ? index : max, 0);
          
        const peakOccupancyHour = hourCounts.reduce((max, hour, index) => 
          hour.occupancy > hourCounts[max].occupancy ? index : max, 0);
        
        return {
          hourCounts,
          peakEntryHour,
          peakExitHour,
          peakOccupancyHour
        };
      })
    );
  }

  // 2. Revenue Summary - Uses historical data
  getRevenueSummary(): Observable<any> {
    return this.parkingLogs$.pipe(
      map(logs => {
        const summary = {
          totalRevenue: 0,
          paidCount: 0,
          unpaidCount: 0,
          discountedCount: 0,
          discountSavings: 0,
          refundedCount: 0,
          refundedAmount: 0,
          cancelledCount: 0,
          netRevenue: 0,
          dailyRevenue: {}
        };

        logs.forEach(log => {
          // Track refunded transactions
          if (log.isRefunded === true) {
            summary.refundedCount++;
            const refundAmount = parseFloat(log.refund_amount || 0);
            summary.refundedAmount += refundAmount;
          }
          
          // Track cancelled transactions
          if (log.isCanceled === true || log.status === 'cancelled') {
            summary.cancelledCount++;
          }

          // Only include paid transactions in revenue calculations
          if (log.paid === true) {
            const amountPaid = parseFloat(log.amount_paid || 0);
            summary.totalRevenue += amountPaid;
            summary.paidCount++;
            
            // Group by day for trend analysis
            if (log.time_in && log.time_in.seconds) {
              const date = new Date(log.time_in.seconds * 1000);
              const dateKey = date.toISOString().split('T')[0];
              
              if (!summary.dailyRevenue[dateKey]) {
                summary.dailyRevenue[dateKey] = 0;
              }
              
              summary.dailyRevenue[dateKey] += amountPaid;
            }
          } else {
            // Count unpaid transactions (but only if not cancelled)
            if (!(log.isCanceled === true || log.status === 'cancelled')) {
              summary.unpaidCount++;
            }
          }
          
          // Track discounted transactions
          if (log.discount_applied) {
            summary.discountedCount++;
            summary.discountSavings += parseFloat(log.discount_savings || 0);
          }
        });
        
        // Calculate net revenue (total revenue minus refunded amount)
        summary.netRevenue = summary.totalRevenue - summary.refundedAmount;
        
        return summary;
      })
    );
  }

  // 3. Vehicle Type Distribution - Uses historical data with transaction type priority
  getVehicleTypeDistribution(): Observable<any> {
    return this.parkingLogs$.pipe(
      map(logs => {
        // Create distribution based on transaction type categories
        const distribution: any = { 
          total: 0,
          categories: {}
        };
        
        // Initialize transaction type categories
        const predefinedCategories = [
          'light_vehicle', // Car (Self Drive)
          'motorcycle', // Motorcycle (Self Drive)
          'valet_light_vehicle', // Valet (Guest)
          'vip_light_vehicle', // Doctor (Paid)
          'vip_free_light_vehicle' // Doctor (Free)
        ];
        
        predefinedCategories.forEach(category => {
          distribution.categories[category] = 0;
        });
        
        // Also add a flat structure for backward compatibility
        predefinedCategories.forEach(category => {
          distribution[category] = 0;
        });
        
        logs.forEach(log => {
          distribution.total++;
          
          // Get the primary category based on transaction type
          const vehicleType = log.vehicle_type?.toLowerCase() || '';
          const transactionType = log.transaction_type;
          const category = this.getTransactionTypeCategory(transactionType, vehicleType);
          
          // Count in the categories object
          if (!distribution.categories[category]) {
            distribution.categories[category] = 0;
          }
          distribution.categories[category]++;
          
          // Also count in flat structure for backward compatibility
          if (!distribution[category]) {
            distribution[category] = 0;
          }
          distribution[category]++;
        });
        
        // Add display names for easier consumption
        distribution.categoryNames = {};
        Object.keys(distribution.categories).forEach(category => {
          distribution.categoryNames[category] = this.getCategoryDisplayName(category);
        });
        
        return distribution;
      })
    );
  }

  // Active Vehicles by Type - Uses real-time data ONLY with transaction type priority
  getActiveVehiclesByType(): Observable<any> {
    return this.activeSessions$.pipe(
      map(logs => {
        // Filter only active and expired sessions within the date range if set
        const activeLogs = logs.filter(log => {
          const isActiveOrExpired = log.session_status === SessionStatusType.Active || 
                                   log.session_status === SessionStatusType.Expired;
          
          // If date range is set, also filter by date
          if (this.startDate && this.endDate && log.timestamp) {
            const logTime = log.timestamp.seconds ? log.timestamp.seconds : log.timestamp._seconds;
            if (logTime) {
              const logTimestamp = logTime * 1000; // Convert to milliseconds
              const startTime = this.startDate.seconds * 1000;
              const endTime = this.endDate.seconds * 1000;
              return isActiveOrExpired && logTimestamp >= startTime && logTimestamp <= endTime;
            }
          }
          
          return isActiveOrExpired;
        });
        
        // Create distribution object based on transaction type categories
        const counts: any = { 
          total: activeLogs.length,
          categories: {}
        };
        
        // Initialize transaction type categories
        const predefinedCategories = [
          'light_vehicle', // Car (Self Drive)
          'motorcycle', // Motorcycle (Self Drive)
          'valet_light_vehicle', // Valet (Guest)
          'vip_light_vehicle', // Doctor (Paid)
          'vip_free_light_vehicle' // Doctor (Free)
        ];
        
        predefinedCategories.forEach(category => {
          counts.categories[category] = 0;
        });
        
        // Also add flat structure for backward compatibility
        predefinedCategories.forEach(category => {
          counts[category] = 0;
        });
        
        activeLogs.forEach(log => {
          // Get the primary category based on transaction type
          const vehicleType = log.vehicle_type?.toLowerCase() || '';
          const transactionType = log.transaction_type;
          const category = this.getTransactionTypeCategory(transactionType, vehicleType);
          
          // Count in categories object
          if (!counts.categories[category]) {
            counts.categories[category] = 0;
          }
          counts.categories[category]++;
          
          // Also count in flat structure for backward compatibility
          if (!counts[category]) {
            counts[category] = 0;
          }
          counts[category]++;
        });
        
        // Calculate percentages
        const percentages: any = {
          categories: {}
        };
        
        Object.keys(counts.categories).forEach(category => {
          percentages.categories[category] = counts.total > 0 ? (counts.categories[category] / counts.total) * 100 : 0;
          // Also add to flat structure
          percentages[category] = percentages.categories[category];
        });
        
        // Add display names for easier consumption
        counts.categoryNames = {};
        Object.keys(counts.categories).forEach(category => {
          counts.categoryNames[category] = this.getCategoryDisplayName(category);
        });
        
        return {
          counts: counts,
          percentages: percentages
        };
      })
    );
  }

  // 4. Session Status Breakdown - Uses historical data
  getSessionStatusBreakdown(): Observable<any> {
    return this.parkingLogs$.pipe(
      map(logs => {
        const statusCounts = {
          active: 0,
          expired: 0,
          completed: 0,
          cancelled: 0,
          voided: 0,
          returned: 0,
          refunded: 0,
          total: logs.length
        };
        
        logs.forEach(log => {
          switch (log.session_status) {
            case SessionStatusType.Active:
              statusCounts.active++;
              break;
            case SessionStatusType.Expired:
              statusCounts.expired++;
              break;
            case SessionStatusType.Completed:
              statusCounts.completed++;
              break;
            case SessionStatusType.Cancelled:
              statusCounts.cancelled++;
              break;
            case SessionStatusType.Voided:
              statusCounts.voided++;
              break;
            case SessionStatusType.Returned:
              statusCounts.returned++;
              break;
            case SessionStatusType.Refunded:
              statusCounts.refunded++;
              break;
            default:
              // Unknown status
              break;
          }
        });
        
        // Calculate percentages
        const statusPercentages = {};
        Object.keys(statusCounts).forEach(key => {
          if (key !== 'total') {
            statusPercentages[key] = statusCounts.total > 0 ? (statusCounts[key] / statusCounts.total) * 100 : 0;
          }
        });
        
        return {
          counts: statusCounts,
          percentages: statusPercentages
        };
      })
    );
  }

  // 5. Average Parking Duration - Uses historical data with transaction types
  getAverageParkingDuration(): Observable<any> {
    return this.parkingLogs$.pipe(
      map(logs => {
        // Only include completed sessions with valid timestamps
        const completedLogs = logs.filter(log => 
          log.session_status === SessionStatusType.Completed && 
          log.time_in && log.time_in.seconds && 
          log.time_out && log.time_out.seconds
        );
        
        // Calculate durations in minutes for each log
        const durations = completedLogs.map(log => {
          const timeIn = log.time_in.seconds;
          const timeOut = log.time_out.seconds;
          return (timeOut - timeIn) / 60; // Convert seconds to minutes
        });
        
        // Calculate average overall
        const calculateAverage = (arr) => arr.length ? 
          arr.reduce((sum, val) => sum + val, 0) / arr.length : 0;
        
        const overallAverage = calculateAverage(durations);
        
        // Calculate average by vehicle type
        const averageByVehicleType: any = {};
        
        // Calculate average by transaction type  
        const averageByTransactionType: any = {};
        
        // Calculate average by combined category (vehicle type + transaction type)
        const averageByCombined: any = {};
            
        // Group logs by vehicle type
        const logsByVehicleType: any = {};
        
        // Group logs by transaction type
        const logsByTransactionType: any = {};
        
        // Group logs by combined category
        const logsByCombined: any = {};
        
        completedLogs.forEach(log => {
          const vehicleType = log.vehicle_type?.toLowerCase() || '';
          const transactionType = log.transaction_type;
          const baseCategory = this.categorizeVehicleType(vehicleType);
          const combinedCategory = this.getTransactionTypeCategory(transactionType, vehicleType);
          
          const timeIn = log.time_in.seconds;
          const timeOut = log.time_out.seconds;
          const duration = (timeOut - timeIn) / 60;
          
          // Group by base vehicle type
          if (!logsByVehicleType[baseCategory]) {
            logsByVehicleType[baseCategory] = [];
          }
          logsByVehicleType[baseCategory].push(duration);
          
          // Group by transaction type
          if (transactionType !== undefined && transactionType !== null) {
            if (!logsByTransactionType[transactionType]) {
              logsByTransactionType[transactionType] = [];
            }
            logsByTransactionType[transactionType].push(duration);
          }
          
          // Group by combined category
          if (!logsByCombined[combinedCategory]) {
            logsByCombined[combinedCategory] = [];
          }
          logsByCombined[combinedCategory].push(duration);
        });
        
        // Calculate average for each vehicle type
        Object.keys(logsByVehicleType).forEach(type => {
          averageByVehicleType[type] = calculateAverage(logsByVehicleType[type]);
        });
        
        // Calculate average for each transaction type
        Object.keys(logsByTransactionType).forEach(type => {
          const transactionType = parseInt(type);
          averageByTransactionType[transactionType] = {
            averageMinutes: calculateAverage(logsByTransactionType[type]),
            typeName: this.getTransactionTypeName(transactionType),
            count: logsByTransactionType[type].length
          };
        });
        
        // Calculate average for each combined category
        Object.keys(logsByCombined).forEach(category => {
          averageByCombined[category] = {
            averageMinutes: calculateAverage(logsByCombined[category]),
            count: logsByCombined[category].length
          };
        });
        
        // Calculate average by zone
        const averageByZone: any = {};
        
        // Group logs by zone
        completedLogs.forEach(log => {
          const zoneId = log.zone_id;
          
          if (zoneId) {
            if (!averageByZone[zoneId]) {
              averageByZone[zoneId] = {
                durations: [],
                averageMinutes: 0,
                zoneName: log.zone_name || `Zone ${zoneId}`
              };
            }
            
            const timeIn = log.time_in.seconds;
            const timeOut = log.time_out.seconds;
            const duration = (timeOut - timeIn) / 60;
            
            averageByZone[zoneId].durations.push(duration);
          }
        });
        
        // Calculate average for each zone
        Object.keys(averageByZone).forEach(zoneId => {
          averageByZone[zoneId].averageMinutes = 
            calculateAverage(averageByZone[zoneId].durations);
        });
        
        return {
          overallAverage,
          averageByVehicleType,
          averageByTransactionType,
          averageByCombined,
          averageByZone,
          totalCompletedSessions: completedLogs.length
        };
      })
    );
  }

  // 6. Zone Utilization - Uses historical data
  getZoneUtilization(): Observable<any> {
    if (!this.account) {
      return of([]);
    }

    return this.parkingLogs$.pipe(
      map(logs => {
        const zones = {};
        
        // First pass: identify zones and their capacities
        logs.forEach(log => {
          const zoneId = log.zone_id;
          if (zoneId && !zones[zoneId]) {
            zones[zoneId] = {
              id: zoneId,
              name: log.zone_name || zoneId,
              capacity: 0, // Will be updated with actual data
              activeCount: 0,
              utilizationPercent: 0
            };
          }
        });
        
        // Count active vehicles per zone
        logs.forEach(log => {
          const zoneId = log.zone_id;
          if (zoneId && zones[zoneId] && log.session_status === SessionStatusType.Active) {
            zones[zoneId].activeCount++;
          }
        });

        // Convert to array for processing
        const zonesArray = Object.values(zones);
        
        // If there are no zones, return empty array
        if (zonesArray.length === 0) {
          return [];
        }

        // Create an array of observables for each zone to get its capacity
        const zoneObservables = zonesArray.map((zone: any) => 
          from(this.zonesSlotService.getSlotsByZoneId(zone.id, this.account.account_key)).pipe(
            map(slots => {
              // Calculate actual capacity from slots
              zone.capacity = slots.length;
              
              // Calculate utilization percentage
              if (zone.capacity > 0) {
                zone.utilizationPercent = Math.min(100, (zone.activeCount / zone.capacity) * 100);
              } else {
                zone.utilizationPercent = 0;
              }
              return zone;
            }),
            catchError(err => {
              // Return zone with default capacity of 0 in case of error
              return of(zone);
            })
          )
        );
        
        // Use forkJoin to wait for all zone capacity queries to complete
        return forkJoin(zoneObservables).pipe(
          map(updatedZones => {
            // Sort zones by utilization percentage (descending) before returning
            return updatedZones.sort((a, b) => b.utilizationPercent - a.utilizationPercent);
          }),
          catchError(err => {
            return of([]);
          })
        );
      }),
      switchMap(result => result),
      catchError(err => {
        return of([]);
      })
    );
  }

  // 7. No-Show / Drop-off Rate - Uses historical data
  getDropoffRate(): Observable<any> {
    return this.parkingLogs$.pipe(
      map(logs => {
        const dailyStats = {};
        
        logs.forEach(log => {
          if (log.time_in?.seconds) {
            const date = new Date(log.time_in.seconds * 1000);
            const dateKey = date.toISOString().split('T')[0];
            
            if (!dailyStats[dateKey]) {
              dailyStats[dateKey] = {
                total: 0,
                incomplete: 0,
                rate: 0
              };
            }
            
            dailyStats[dateKey].total++;
            
            if (log.status === 'incomplete' || !log.time_out) {
              dailyStats[dateKey].incomplete++;
            }
          }
        });
        
        // Calculate drop-off rates
        Object.keys(dailyStats).forEach(date => {
          if (dailyStats[date].total > 0) {
            dailyStats[date].rate = 
              (dailyStats[date].incomplete / dailyStats[date].total) * 100;
          }
        });
        
        // Calculate overall drop-off rate
        const overall = {
          total: logs.length,
          incomplete: logs.filter(log => log.status === 'incomplete' || !log.time_out).length,
          rate: 0
        };
        
        if (overall.total > 0) {
          overall.rate = (overall.incomplete / overall.total) * 100;
        }
        
        // Check if threshold exceeded (10%)
        const thresholdExceeded = overall.rate > 10;
        
        return {
          dailyStats,
          overall,
          thresholdExceeded
        };
      })
    );
  }

  // Method to start real-time listener for active sessions only
  startRealtimeUpdates(accountKey?: string) {
    // Clean up existing listener if any
    if (this.firestoreUnsubscribe) {
      this.firestoreUnsubscribe();
    }
    
    const accountToUse = accountKey || (this.account ? this.account.account_key : null);
    if (!accountToUse) {
      this.setLoading(false);
      return;
    }


    // Create base query for active sessions (real-time)
    let activeQuery = firebase.firestore().collection('logs')
      .where('account_key', '==', accountToUse);
    
    // Add zone filtering if we have a zone ID that isn't 'dashboard'
    if (this.zone_id && this.zone_id !== 'dashboard') {
      activeQuery = activeQuery.where('zone_id', '==', this.zone_id);
    }
    
    // Use 'in' operator to filter for both active and expired sessions
    activeQuery = activeQuery.where('session_status', 'in', [SessionStatusType.Active, SessionStatusType.Expired]);

    // Set up real-time snapshot listener for active sessions
    this.firestoreUnsubscribe = activeQuery.onSnapshot((snapshot) => {
      if (snapshot.empty) {
        this.activeSessionsData.next([]);
        return;
      }

      const activeLogs = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        data.id = doc.id;
        activeLogs.push(data);
      });
      
      this.activeSessionsData.next(activeLogs);
    }, (error) => {
      this.setLoading(false);
    });
    
    // Load historical data for analytics
    this.loadHistoricalData(accountToUse);
  }
  
  // Method to start real-time listener for historical data analytics
  private loadHistoricalData(accountKey: string) {
    // Prevent concurrent historical data loading and check for duplicate calls
    if (this.isLoadingHistorical) {
      return;
    }
    
    // Compare new date range with the one that's already loaded
    const isDateRangeEqual = (date1: firebase.firestore.Timestamp, date2: firebase.firestore.Timestamp) => {
      return date1 && date2 && date1.seconds === date2.seconds && date1.nanoseconds === date2.nanoseconds;
    };
    
    // If we already have data for this exact time period and the same zone, avoid reloading
    if (this.historicalUnsubscribe && 
        this.parkingLogsData.value.length > 0 &&
        isDateRangeEqual(this.startDate, this._lastLoadedStartDate) &&
        isDateRangeEqual(this.endDate, this._lastLoadedEndDate) &&
        this.zone_id === this._lastLoadedZoneId) {
      this.setLoading(false);
      return;
    }
    
    // Store the date range we're loading for future reference
    this._lastLoadedStartDate = this.startDate;
    this._lastLoadedEndDate = this.endDate;
    this._lastLoadedZoneId = this.zone_id;
    
    this.isLoadingHistorical = true;
    
    // Clean up existing historical listener if any
    if (this.historicalUnsubscribe) {
      this.historicalUnsubscribe();
      this.historicalUnsubscribe = null;
    }
    
    
    // Create query for historical data
    let historicalQuery = firebase.firestore().collection('logs')
      .where('account_key', '==', accountKey);
      
    // Add zone filtering if we have a zone ID that isn't 'dashboard'
    if (this.zone_id && this.zone_id !== 'dashboard') {
      historicalQuery = historicalQuery.where('zone_id', '==', this.zone_id);
    }
    
    // Add timeframe filtering if dates are set
    if (this.startDate && this.endDate) {
      historicalQuery = historicalQuery
        .where('timestamp', '>=', this.startDate)
        .where('timestamp', '<=', this.endDate);
    } else {
      // Limit to recent logs (last 30 days) if no specific dates set
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysAgoTimestamp = firebase.firestore.Timestamp.fromDate(thirtyDaysAgo);
      historicalQuery = historicalQuery.where('timestamp', '>=', thirtyDaysAgoTimestamp);
    }
    
    // Set up real-time snapshot listener for historical data
    this.historicalUnsubscribe = historicalQuery.onSnapshot((snapshot) => {
      if (snapshot.empty) {
        this.parkingLogsData.next([]);
        this.setLoading(false);
        this.isLoadingHistorical = false;
        return;
      }
      
      const historicalLogs = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        data.id = doc.id;
        historicalLogs.push(data);
      });
      
      this.parkingLogsData.next(historicalLogs);
      this.setLoading(false);
      this.isLoadingHistorical = false;
    }, (error) => {
      this.setLoading(false);
      this.isLoadingHistorical = false;
    });
  }

  // Method to stop real-time listeners
  stopRealtimeUpdates() {
    if (this.firestoreUnsubscribe) {
      this.firestoreUnsubscribe();
      this.firestoreUnsubscribe = null;
    }
    if (this.historicalUnsubscribe) {
      this.historicalUnsubscribe();
      this.historicalUnsubscribe = null;
    }
    
    // Clear any pending timeouts and reset flags
    if (this.setDateRangeTimeout) {
      clearTimeout(this.setDateRangeTimeout);
      this.setDateRangeTimeout = null;
    }
    
    // Reset last loaded state
    this._lastLoadedStartDate = null;
    this._lastLoadedEndDate = null;
    this._lastLoadedZoneId = null;
    
    this.isLoadingHistorical = false;
  }
  
  // Special method to finalize a one-month selection and prevent further updates
  finalizeMonthRange(startDate: firebase.firestore.Timestamp, endDate: firebase.firestore.Timestamp, account_key: string): void {
    // Store the date range to avoid additional loads
    this._lastLoadedStartDate = startDate;
    this._lastLoadedEndDate = endDate;
    this._lastLoadedZoneId = this.zone_id;
    
    // No need to reload, just emit event for components to refresh
    this.dateRangeChanged.emit();
    
  }
  
  // Method to refresh historical data when date range changes
  refreshHistoricalData() {
    if (this.account && !this.isLoadingHistorical) {
      this.setLoading(true);
      this.loadHistoricalData(this.account.account_key);
    }
  }
} 